import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { useIntl } from 'react-intl-next';
import { IconButton, LinkIconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import DepartmentIcon from '@atlaskit/icon/core/department';
import EmailIcon from '@atlaskit/icon/core/email';
import LocationIcon from '@atlaskit/icon/core/location';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import TeamsAvatar from '@atlaskit/teams-avatar';
import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { messages } from '../messages';
import { onKnowledgeCardLinkClick } from './analytics';
import { KnowledgeCardsTopics } from './knowledge-cards-topics';
import { KnowledgeCardsPicture } from './styled';
import { KnowledgeCardsIntentType } from './types';
import { onEmailCopy } from './util';
var KnowledgeCardsPersonHeaderWrapper = xcss({
  padding: 'space.250'
});
var KnowledgeCardsTeamHeaderWrapper = xcss({
  paddingTop: 'space.150',
  paddingLeft: 'space.250',
  paddingRight: 'space.250',
  paddingBottom: 'space.150'
});
var KnowledgeCardsPrimaryInfoWrapper = xcss({
  display: 'flex',
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  justifyContent: 'center'
});
var KnowledgeCardsButtonsWrapper = xcss({
  paddingTop: 'space.100'
});
export var KnowledgeCardsHeader = function KnowledgeCardsHeader(_ref) {
  var type = _ref.type,
    name = _ref.name,
    accountId = _ref.accountId,
    email = _ref.email,
    avatarUrl = _ref.avatarUrl,
    profileUrl = _ref.profileUrl,
    jobTitle = _ref.jobTitle,
    department = _ref.department,
    location = _ref.location,
    time = _ref.time;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var isPerson = type === KnowledgeCardsIntentType.PERSON;
  var subText = isPerson ? [jobTitle, department].filter(function (text) {
    return !!text;
  }).join(', ') : formatMessage(messages.knowledge_cards_team_subheader);
  var trimmedSubText = subText.length > 250 ? "".concat(subText.slice(0, 250), "...") : subText;
  var timeAndLocationComponent = isPerson && (location || time) && /*#__PURE__*/React.createElement(Text, {
    size: "medium",
    color: "color.text.subtlest"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(LocationIcon, {
    label: ""
  }), /*#__PURE__*/React.createElement(Inline, {
    space: "space.050"
  }, location && /*#__PURE__*/React.createElement(Text, null, location), time && location && /*#__PURE__*/React.createElement(Text, null, "\u2022"), time && /*#__PURE__*/React.createElement(Text, null, time))));
  var tooltipOptions = {
    position: 'bottom',
    content: formatMessage(messages.knowledge_cards_copy_email_address),
    hideTooltipOnClick: true
  };
  var cloudId = commonAttributes.cloudId,
    sessionId = commonAttributes.sessionId;
  var showAITopics = isPerson && fg('platform_search-ai_related-entities');
  var viewText = isPerson ? messages.ai_answer_who_questions_view_profile : messages.knowledge_cards_view_team;
  return /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    xcss: isPerson ? KnowledgeCardsPersonHeaderWrapper : KnowledgeCardsTeamHeaderWrapper
  }, /*#__PURE__*/React.createElement(Inline, {
    spread: "space-between",
    alignInline: "start",
    alignBlock: "center",
    grow: "fill"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.200",
    alignBlock: "center"
  }, !isPerson && fg('team-avatar-search-card') ? /*#__PURE__*/React.createElement(TeamsAvatar, {
    size: "xlarge",
    src: avatarUrl || undefined,
    name: name
  }) : /*#__PURE__*/React.createElement(KnowledgeCardsPicture, {
    src: avatarUrl || '',
    alt: name
  }), /*#__PURE__*/React.createElement(Box, {
    xcss: KnowledgeCardsPrimaryInfoWrapper
  }, /*#__PURE__*/React.createElement(Stack, {
    space: "space.100"
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "medium"
  }, name), trimmedSubText && /*#__PURE__*/React.createElement(Text, {
    size: "medium",
    color: "color.text.subtlest"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    alignBlock: "center"
  }, isPerson && /*#__PURE__*/React.createElement(DepartmentIcon, {
    label: ""
  }), " ", /*#__PURE__*/React.createElement(Text, null, trimmedSubText))), timeAndLocationComponent, showAITopics && /*#__PURE__*/React.createElement(KnowledgeCardsTopics, {
    cloudId: cloudId,
    userId: accountId !== null && accountId !== void 0 ? accountId : '',
    userName: name,
    sessionId: sessionId,
    fallbackSection: /*#__PURE__*/React.createElement(React.Fragment, null),
    v2styles: true
  }))))), /*#__PURE__*/React.createElement(Inline, {
    space: "space.050",
    xcss: KnowledgeCardsButtonsWrapper
  }, !!email && /*#__PURE__*/React.createElement(IconButton, {
    icon: function icon(props) {
      return /*#__PURE__*/React.createElement(EmailIcon, _extends({}, props, {
        color: "var(--ds-icon, #44546F)"
      }));
    },
    label: formatMessage(messages.ai_answer_who_questions_hover_email_icon),
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'header-copy-email'));
      fireSAINUsageEvent({
        metCriteria: SainAction.COPY_CLICKED
      });
      onEmailCopy(email);
    },
    isTooltipDisabled: false,
    tooltip: tooltipOptions
  }), /*#__PURE__*/React.createElement(LinkIconButton, {
    icon: ArrowRightIcon,
    label: formatMessage(viewText),
    href: profileUrl || '',
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'header-view-profile'));
      fireSAINUsageEvent({
        metCriteria: SainAction.VIEW_PROFILE_CLICKED
      });
    }
  })));
};